import React from 'react';
import * as yup from 'yup';
import { I18n } from '@lingui/core';
import { t } from '@lingui/macro';

import {
  RegistrationFieldType,
  RegistrationFormField,
  RegistrationFormSettingsFragment,
  SelectionType,
  SessionStatus,
} from '$/GraphQL/schema/schema';
import { BuiltInProperties } from '$/GraphQL/schema/builtInProperties';
import { PageMode } from '$/components/ui/Input/PageBuilder.types';
import { isValidRegisterButtonText } from '$/components/ui/Input/CTAButton/isValidRegisterButtonText';

import EDate from '$/shared/frontend/utils/EDate';
import config from '$/config';

import { usePageEditorContext } from '../PageEditorContext';

export const useRegistrationFormValidationSchemaPublicMode = ({
  i18n,
  refreshEwebinarSession,
  registrationFormSettings,
  setShowConditionsPopup,
}: {
  i18n?: I18n;
  registrationFormSettings: RegistrationFormSettingsFragment;
  refreshEwebinarSession?: () => void;
  setShowConditionsPopup?: (show: boolean) => void;
}) => {
  const validationSchema = React.useMemo(() => {
    if (!i18n) return;
    const emailFieldSchema = yup
      .string()
      .trim()
      .email(t(i18n)`This must be a valid email address`);
    const requiredEmailFieldSchema = emailFieldSchema.required(t(i18n)`This field is required`);
    const textFieldSchema = yup
      .string()
      .trim()
      .max(50, t(i18n)`Must be at most 50 characters`)
      .required(t(i18n)`This field is required`);

    // TODO: propertyName is required when Type = Hidden
    const attendeeFieldsSchema = yup
      .array()
      .of(
        yup.lazy((field: RegistrationFormField) => {
          if (!field?.isRemovable) {
            // Handled already as firstName, lastName, email
            return yup.object<RegistrationFormField>();
          }

          if (field?.type === RegistrationFieldType.Email) {
            return yup.object<RegistrationFormField>().shape({
              value: field.isRequired ? requiredEmailFieldSchema : emailFieldSchema,
            });
          } else if (field?.type === RegistrationFieldType.Phone) {
            return yup.object<RegistrationFormField>().shape({
              value: yup
                .string()
                .test(
                  'required',
                  t(i18n)`This field is required`,
                  (value) => !field.isRequired || Boolean(value)
                )
                .test('phone', t(i18n)`This must be a valid phone number`, async (value) => {
                  if (!value) return true;
                  const isValid = await import('libphonenumber-js').then(({ parsePhoneNumber }) => {
                    try {
                      const phoneNumber = parsePhoneNumber(value);
                      return phoneNumber?.isValid() ?? false;
                    } catch (e) {
                      return false;
                    }
                  });
                  return isValid;
                }),
            });
          } else if (field?.isRequired) {
            if (
              field.type === RegistrationFieldType.Selection &&
              field?.subType === SelectionType.Multi
            ) {
              return yup.object<RegistrationFormField>().shape({
                value: yup
                  .array()
                  .of(yup.string())
                  .min(1, t(i18n)`This field is required`),
              });
            }
            return yup.object<RegistrationFormField>().shape({ value: textFieldSchema });
          }
          return yup.object<RegistrationFormField>();
        })
      )
      .required(t(i18n)`This field is required`);

    const sessionSchema = yup
      .object()
      .test('required', t(i18n)`This field is required`, (value) => Boolean(value))
      .test(
        'not full',
        t(i18n)`The session you selected is full.`,
        function (value?: any): boolean {
          if (value?.status === SessionStatus.Full) {
            return false;
          }
          return true;
        }
      )
      .test(
        'not in the past',
        t(i18n)`The session you selected is in the past or has already begun.`,
        function test(value?: any | null) {
          if (!value) {
            return false;
          }
          if (value.value === 'replay') {
            return true;
          }
          try {
            const startTime = new EDate(
              value.date instanceof Date ? value.date?.toISOString() : value.date
            ).addMinutes(config.MINUTES_ALLOW_TO_REGISTER_AFTER_WEBINAR_START); // Allow attendees to register for a session up to 5 minutes AFTER session starts
            if (startTime.isAfter(new EDate(new Date().toISOString()))) {
              return true;
            }
          } catch (e) {
            // return false after refreshEwebinarSession;
          }
          refreshEwebinarSession?.();
          return false;
        }
      )
      .typeError(t(i18n)`This field is required`);

    return yup.object().shape({
      session: sessionSchema,
      name: yup.string().when('useFullNameField', {
        is: true,
        then: (schema) => schema.trim().required(t(i18n)`This field is required`),
      }),
      firstName: yup.string().when('useFullNameField', {
        is: false,
        then: (schema) => schema.trim().required(t(i18n)`This field is required`),
      }),
      lastName: yup.string().when('useFullNameField', {
        is: false,
        then: () =>
          registrationFormSettings.fields[1].isRequired
            ? yup
                .string()
                .trim()
                .required(t(i18n)`This field is required`)
            : yup.string().nullable(),
      }),
      email: requiredEmailFieldSchema,
      attendeeFields: attendeeFieldsSchema,
      checkedConsent: yup.boolean().when('showConsentCheckbox', {
        is: true,
        then: () =>
          yup.boolean().test('Validate checkedConsent to show Popup', '', function test(val: any) {
            if (val === false) {
              setShowConditionsPopup?.(true);
            } else {
              setShowConditionsPopup?.(false);
            }
            return val;
          }),
      }),
    });
  }, [i18n, refreshEwebinarSession, registrationFormSettings.fields, setShowConditionsPopup]);
  return validationSchema;
};

export const useRegistrationFormValidationSchemaEditMode = ({
  i18n,
  usedProperties: usedPropertiesProps,
}: {
  i18n?: I18n;
  usedProperties?: string[];
}) => {
  const validationSchema = React.useMemo(() => {
    if (!i18n) return;
    const fieldsSchema = yup.array().of(
      yup.object().shape({
        fieldName: yup.string().when('type', {
          is: (value: RegistrationFieldType) => value !== RegistrationFieldType.Hidden,
          then: (schema) => schema.required(),
        }),
        propertyName: yup
          .string()
          .nullable()
          .trim()
          .when(['type', 'isRemovable'], {
            is: (value: RegistrationFieldType, isRemovable: boolean) =>
              isRemovable && value === RegistrationFieldType.Hidden,
            then: (schema) => schema.required(),
          })
          .matches(/^([A-Za-z][A-Za-z0-9_]*)?$/, 'May only contain numbers, letters and _'),
      })
    );

    const propertyTest = (
      values: RegistrationFormField[],
      testContext: yup.TestContext
    ): true | yup.ValidationError => {
      // should skip for firstName, lastName and email fields
      const validateFieldFromIndex = 3;
      const formProperties = values
        .slice(validateFieldFromIndex)
        .map((f) => (f.propertyName ?? '').toLowerCase())
        .filter((p) => p !== '');

      const builtInProperties = BuiltInProperties.map((p) => p.toLowerCase());
      const usedProperties = [...new Set([...builtInProperties, ...(usedPropertiesProps ?? [])])];
      const properties = [...usedProperties, ...formProperties];
      const uniqueProperties = [...new Set(properties)];
      const invalid = properties.length !== uniqueProperties.length;
      if (invalid) {
        const unavailableProperties = [...usedProperties];
        for (let idx = validateFieldFromIndex; idx < values.length; idx++) {
          const propertyName = values[idx].propertyName?.toLowerCase();
          if (!propertyName) continue;
          if (!unavailableProperties.includes(propertyName)) {
            unavailableProperties.push(propertyName);
            continue;
          }
          const path = `${testContext.path}.${idx}.propertyName`;
          return testContext.createError({
            path,
            message: 'Property name must be unique across fields and interactions',
          });
        }
      }

      // require options for dropdown
      const invalidDropdown = values.findIndex(
        (f) =>
          f?.type === RegistrationFieldType.Selection && (!f?.options || f?.options.length === 0)
      );
      if (invalidDropdown !== -1) {
        const path = `${testContext.path}.${invalidDropdown}.propertyName`;
        return testContext.createError({
          path,
          message: 'The dropdown options are required.',
        });
      }

      return true;
    };

    return yup.object().shape({
      title: yup.string().required(),
      fields: fieldsSchema.test('unique', 'unique property name', function test(values?: any[]) {
        return propertyTest((values as RegistrationFormField[]) ?? [], this);
      }),
      showConsentCheckbox: yup.boolean().required(),
      consentCheckboxText: yup.string().when('showConsentCheckbox', {
        is: true,
        then: (schema) => schema.required(),
      }),
      registerButton: yup
        .string()
        .test('maxCharsRegisterBtn', async function test(value?: string): Promise<boolean> {
          return isValidRegisterButtonText(value);
        }),
    });
  }, [i18n, usedPropertiesProps]);
  return validationSchema;
};

export const useRegistrationFormValidationSchema = ({
  registrationFormSettings,
  refreshEwebinarSession,
  setShowConditionsPopup,
  usedProperties,
  i18n,
}: {
  i18n?: I18n;
  usedProperties?: string[];
  registrationFormSettings: RegistrationFormSettingsFragment;
  refreshEwebinarSession?: () => void;
  setShowConditionsPopup?: (show: boolean) => void;
}) => {
  const { mode } = usePageEditorContext();
  const editModeValidationSchema = useRegistrationFormValidationSchemaEditMode({
    i18n,
    usedProperties,
  });
  const publicModeValidationSchema = useRegistrationFormValidationSchemaPublicMode({
    i18n,
    registrationFormSettings,
    refreshEwebinarSession,
    setShowConditionsPopup,
  });

  const validationSchema = React.useMemo(() => {
    return mode === PageMode.edit ? editModeValidationSchema : publicModeValidationSchema;
  }, [mode, editModeValidationSchema, publicModeValidationSchema]);
  return validationSchema;
};
